import React, { useEffect, useState, useMemo, useCallback } from 'react';
import MapGL, { Marker } from 'react-map-gl';
import LocationOnIcon from '@material-ui/icons/LocationOn';
import FileCopyOutlinedIcon from '@material-ui/icons/FileCopyOutlined';

import Geolocation from '../../../../services/GeolocationService/geolocationService';

import * as S from './Maps.styles';
import { IMaps } from './Maps.types';
import {
  formatCoordinate,
  hasValidCoordinates,
  clusterMarkers,
  copyToClipboard,
  formatCoordinatesForClipboard,
  IClusteredMarker,
} from './Maps.utils';

const MAPBOX_TOKEN = process.env.REACT_APP_MAPBOX_TOKEN;
const CLUSTER_DISTANCE_METERS = 100; // 100 metros de distância mínima para agrupar

/**
 * Componente interno para renderizar marcador customizado com label de coordenadas
 */
interface ICustomMarkerProps {
  cluster: IClusteredMarker;
  index: number;
}

const CustomMarker: React.FC<ICustomMarkerProps> = ({ cluster, index }) => {
  const { latitude, longitude, count } = cluster;
  const [copied, setCopied] = useState(false);

  const formattedCoordinates = useMemo(
    () => ({
      lat: formatCoordinate(latitude),
      lng: formatCoordinate(longitude),
      isValid: hasValidCoordinates(latitude, longitude),
    }),
    [latitude, longitude]
  );

  const handleCopyCoordinates = useCallback(
    async (e: React.MouseEvent) => {
      e.stopPropagation();

      const coordinatesText = formatCoordinatesForClipboard(latitude, longitude);
      const success = await copyToClipboard(coordinatesText);

      if (success) {
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      }
    },
    [latitude, longitude]
  );

  if (!formattedCoordinates.isValid) {
    return null;
  }

  const totalPhotos = cluster.photos.reduce((sum, photo) => sum + photo.photos.length, 0);
  const hasCluster = count > 1;

  return (
    <S.MarkerWrapper data-testid={`marker-wrapper-${index}`}>
      <S.CoordinateLabel
        data-testid={`coordinate-label-${index}`}
        onClick={handleCopyCoordinates}
        $copied={copied}
        title="Clique para copiar coordenadas"
      >
        <S.LabelHeader>
          <S.LabelTitle>Localização</S.LabelTitle>
          {hasCluster && <S.ClusterBadge>{count} locais</S.ClusterBadge>}
        </S.LabelHeader>

        <S.CoordinateRow>
          <S.CoordinateKey>Lat:</S.CoordinateKey>
          <S.CoordinateValue>{formattedCoordinates.lat}</S.CoordinateValue>
        </S.CoordinateRow>

        <S.CoordinateRow>
          <S.CoordinateKey>Lng:</S.CoordinateKey>
          <S.CoordinateValue>{formattedCoordinates.lng}</S.CoordinateValue>
        </S.CoordinateRow>

        <S.CopyHint $copied={copied}>
          <FileCopyOutlinedIcon style={{ fontSize: 10 }} />
          {copied ? 'Copiado!' : 'Clique para copiar'}
        </S.CopyHint>
      </S.CoordinateLabel>

      <S.IconWrapper $hasCluster={hasCluster}>
        <LocationOnIcon color="primary" fontSize="large" />
        {hasCluster && <S.ClusterCountBadge>{totalPhotos}</S.ClusterCountBadge>}
      </S.IconWrapper>
    </S.MarkerWrapper>
  );
};

const Maps: React.FC<IMaps> = ({ geolocationPhotos, height, width }) => {
  const [viewport, setViewport] = useState({
    latitude: 0,
    longitude: 0,
    zoom: 14,
  });

  useEffect(() => {
    const { latitude, longitude } = Geolocation.getInitialCoordinatesOfGeolocationPhotos(geolocationPhotos);

    setViewport((value) => ({ ...value, latitude, longitude }));
  }, [geolocationPhotos]);

  // Filtra apenas marcadores com coordenadas válidas
  const validMarkers = useMemo(
    () => geolocationPhotos.filter((photo) => hasValidCoordinates(photo.latitude, photo.longitude)),
    [geolocationPhotos]
  );

  // Agrupa marcadores próximos
  const clusteredMarkers = useMemo(() => clusterMarkers(validMarkers, CLUSTER_DISTANCE_METERS), [validMarkers]);

  return (
    <S.Container data-testid="map-container" height={height} width={width}>
      <MapGL
        mapboxApiAccessToken={MAPBOX_TOKEN}
        {...viewport}
        width="100%"
        height="100%"
        onViewportChange={setViewport}
        mapStyle="mapbox://styles/mapbox/outdoors-v11"
      >
        {clusteredMarkers.map((cluster, index) => (
          <Marker
            key={`cluster-${index}-${cluster.latitude}-${cluster.longitude}`}
            latitude={cluster.latitude}
            longitude={cluster.longitude}
          >
            <CustomMarker cluster={cluster} index={index} />
          </Marker>
        ))}
      </MapGL>
    </S.Container>
  );
};

export default Maps;
